import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useTour } from '../../contexts/TourContext';
import { useIsCustomer } from '../../hooks/usePortalMode';
import {
  ArrowRight,
  BookOpen,
  Rocket,
} from 'lucide-react';

interface NavigationMapping {
  old: string;
  new: string;
  location: string;
  notes?: string;
}

const SUPPLIER_NAV_MAPPINGS: NavigationMapping[] = [
  { old: 'Home / Dashboard', new: 'Dashboard', location: 'Left sidebar', notes: 'Click charts to drill down' },
  { old: 'Sales Orders > PO Overview', new: 'Sales Orders', location: 'Left sidebar', notes: 'Use tabs for SO Headers, SO Lines and ASN' },
  { old: 'Sales Orders > SO Lines', new: 'Sales Orders > SO Lines tab', location: 'Left sidebar' },
  { old: 'Sales Orders > ASN', new: 'Sales Orders > ASN tab', location: 'Left sidebar', notes: 'Create and track Advanced Shipping Notices' },
  { old: 'Quality > NCRs', new: 'Quality', location: 'Left sidebar', notes: 'Use tabs for NCRs/Concessions' },
  { old: 'Customers List', new: 'Customers', location: 'Left sidebar' },
  { old: 'Customer Profile', new: 'Customers > Click customer', location: 'Detail page' },
  { old: 'Reports / Export', new: 'Each page has export', location: 'Page actions', notes: 'Look for Export button' },
  { old: 'User Settings', new: 'Settings', location: 'Left sidebar (bottom)' },
  { old: 'Admin Panel', new: 'Admin Setup', location: 'Left sidebar (bottom)' },
];

const CUSTOMER_NAV_MAPPINGS: NavigationMapping[] = [
  { old: 'Home / Dashboard', new: 'Dashboard', location: 'Left sidebar', notes: 'Your orders, deliveries and quality metrics' },
  { old: 'My POs', new: 'Sales Orders', location: 'Left sidebar', notes: 'View and acknowledge orders from your supplier' },
  { old: 'SO Lines', new: 'Sales Orders > SO Lines tab', location: 'Left sidebar', notes: 'Acknowledge lines or propose changes' },
  { old: 'View ASN', new: 'Sales Orders > ASN tab', location: 'Left sidebar', notes: 'Track shipments from your supplier' },
  { old: 'Quality Issues', new: 'Quality', location: 'Left sidebar', notes: 'View NCRs and concessions' },
  { old: 'Documents', new: 'Documents', location: 'Left sidebar', notes: 'Upload and download shared files' },
  { old: 'News / Announcements', new: 'News', location: 'Left sidebar' },
  { old: 'User Settings', new: 'Settings', location: 'User menu (top-right)' },
];

export function QuickReferenceModal() {
  const { showQuickReference, setShowQuickReference, startTour } = useTour();
  const isCustomer = useIsCustomer();
  const mappings = isCustomer ? CUSTOMER_NAV_MAPPINGS : SUPPLIER_NAV_MAPPINGS;

  const handleStartTour = () => {
    setShowQuickReference(false);
    startTour();
  };

  return (
    <Dialog open={showQuickReference} onOpenChange={setShowQuickReference}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle>Quick Reference Guide</DialogTitle>
              <DialogDescription>
                Old portal navigation → New Helix navigation
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              <strong>Key Change:</strong> Main navigation moved from top menu bar to left sidebar.
              The sidebar can be collapsed by clicking the arrow icon.
            </p>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b">
                  <th className="text-left py-2 px-3 font-medium">Old Portal</th>
                  <th className="text-center py-2 px-3 font-medium w-8"></th>
                  <th className="text-left py-2 px-3 font-medium">Helix</th>
                  <th className="text-left py-2 px-3 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, idx) => (
                  <tr key={idx} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="py-2 px-3 text-muted-foreground">{mapping.old}</td>
                    <td className="py-2 px-3 text-center">
                      <ArrowRight className="h-4 w-4 text-blue-500 inline" />
                    </td>
                    <td className="py-2 px-3 font-medium">{mapping.new}</td>
                    <td className="py-2 px-3">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {mapping.location}
                      </span>
                      {mapping.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{mapping.notes}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-3">
            <h4 className="font-medium text-sm">Other Key Changes</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Tabs Inside Pages</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  {isCustomer
                    ? 'Sales Orders uses tabs for SO Lines and ASN — switch between them at the top of the page.'
                    : 'Sales Orders and Customers pages use tabs to organise related views (SO Headers, SO Lines, ASN).'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Click-to-Drill-Down</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Dashboard charts are interactive. Click on data points to see detailed breakdowns.
                </p>
              </div>
              {!isCustomer && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">View Selector</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the dropdown in the top bar to switch between Site, Company, and Group views.
                </p>
              </div>
              )}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Notifications</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Bell icon in top bar shows all notifications. Click to view and manage alerts.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => setShowQuickReference(false)}>
            Close
          </Button>
          <Button onClick={handleStartTour}>
            <Rocket className="h-4 w-4 mr-2" />
            Take Interactive Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
